/**
 * Hooks para Documentos Anexos por club a un evento.
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-hot-toast";
import { useRealtimeList } from "@/hooks/useRealtime";
import {
  subscribeToEventClubAttachments,
  subscribeToAllEventAttachments,
  uploadEventClubAttachment,
  deleteEventClubAttachment,
  type UploadAttachmentInput,
} from "@/lib/services/eventAttachments";
import type { EventClubAttachment } from "@/types";

export function useEventClubAttachments(
  eventId: string | undefined,
  clubId: string | undefined,
) {
  return useRealtimeList<EventClubAttachment>(
    eventId && clubId
      ? (cb, onErr) =>
          subscribeToEventClubAttachments(eventId, clubId, cb, onErr)
      : null,
    [eventId, clubId],
  );
}

export function useAllEventAttachments(eventId: string | undefined) {
  return useRealtimeList<EventClubAttachment>(
    eventId
      ? (cb, onErr) => subscribeToAllEventAttachments(eventId, cb, onErr)
      : null,
    [eventId],
  );
}

export function useUploadEventClubAttachment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: UploadAttachmentInput) =>
      uploadEventClubAttachment(input),
    onSuccess: () => {
      toast.success("Documento subido");
      queryClient.invalidateQueries({ queryKey: ["eventClubAttachments"] });
    },
    onError: (err: Error) => {
      toast.error(err.message || "Error al subir documento");
    },
  });
}

export function useDeleteEventClubAttachment() {
  return useMutation({
    mutationFn: (attachment: EventClubAttachment) =>
      deleteEventClubAttachment(attachment),
    onSuccess: () => {
      toast.success("Documento eliminado");
    },
    onError: (err: Error) => {
      toast.error(err.message || "Error al eliminar");
    },
  });
}
